import { FC, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    href: {
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'var(--ion-color-primary)',
        '&:hover': {
            textDecoration: 'underline'
        }
    }
});

export type IHrefProps = {
    url?: string;
    onClick?: () => void;
    children: ReactNode;
    target?: '_blank' | '_self';
    'data-id'?: string;
};

export const Href: FC<IHrefProps> = ({ url, onClick, children, target = '_blank', 'data-id': dataId }) => {
    const classes = useStyles();

    if (!url && !onClick) {
        throw new Error('Either url or onClick is required');
    }

    const handleClick = (e: MouseEvent) => {
        e.stopPropagation();

        if (onClick) {
            onClick();
        }
    };

    // An anchor with no href is not in the tab order and does not fire on Enter, so a link that
    // only runs an onClick cannot be reached without a mouse while still looking clickable to
    // everyone else. There is no url to put in the href either, because the handler IS the
    // destination — so this is given the semantics it actually has, a button, rather than the link
    // it is styled as. That is why a screen reader announces "button" here, and why Space activates
    // it, which a real link would not do.
    const keyboardProps = url ? {} : {
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e: KeyboardEvent) => {
            if (e.key !== 'Enter' && e.key !== ' ') {
                return;
            }
            // Suppressed before the repeat guard below, not after: Space scrolls the page on every
            // keydown including repeats, so returning early first would fire the handler once and
            // then scroll for as long as the key was held.
            e.preventDefault();
            e.stopPropagation();

            // Holding a key auto-repeats keydown, and this activates on keydown rather than keyup,
            // so without this a held key would run the handler over and over. That suppresses
            // held-Enter repeat too, which a native button does allow — deliberate here, because
            // these handlers are one-shot: they open a tab, a drawer or a share sheet, and
            // repeating one is never what the caregiver meant.
            if (e.repeat) {
                return;
            }

            onClick?.();
        }
    };

    return (
        // Modern browsers imply noopener on a target=_blank anchor, but stating it removes the
        // dependency on which engine is running — the app ships inside Capacitor WebViews as well
        // as desktop browsers. noreferrer is deliberate too: these urls now include ones a
        // caregiver typed into a task, so the destination has no business learning where they came
        // from.
        <a data-id={dataId} href={url} target={target} rel="noopener noreferrer" className={classes.href} onClick={handleClick} {...keyboardProps}>{children}</a>
    );
};
